import { useEffect, useRef, useState } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import useListHighlight from '../hooks/useListHighlight';

function OrgSearchPicker({
  orgQuery,
  setOrgQuery,
  orgs,
  onSearch,
  onSelect,
  clearOrgs,
  placeholder = 'Search for an organization',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const blurTimerRef = useRef(null);
  const hasResults = orgs && orgs.length > 0;
  const showResults = isOpen && hasResults;
  const { highlightedIndex, setHighlightedIndex, hoverHighlight, registerOption, handleArrowKey } = useListHighlight(orgs?.length ?? 0);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [orgs, setHighlightedIndex]);

  useEffect(() => {
    if (!orgQuery.trim()) {
      clearOrgs();
      return;
    }
    const timer = setTimeout(() => onSearch(), 300);
    return () => clearTimeout(timer);
  }, [orgQuery]);

  useEffect(() => () => clearTimeout(blurTimerRef.current), []);

  const handleFocus = () => {
    clearTimeout(blurTimerRef.current);
    setIsOpen(true);
  };

  const handleBlur = () => {
    blurTimerRef.current = setTimeout(() => setIsOpen(false), 150);
  };

  const handleKeyDown = e => {
    if (!showResults) {
      if (hasResults && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        e.preventDefault();
        setIsOpen(true);
        return;
      }
      if (e.key === 'Enter') {
        setIsOpen(true);
        onSearch();
      }
      return;
    }

    if (handleArrowKey(e)) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0) {
        onSelect(orgs[highlightedIndex]);
      } else {
        onSearch();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
    }
  };

  return (
    <div className="relative max-w-md w-full">
      <label className="input h-8 flex items-center gap-2 border border-secondary-red/30 hover:border-secondary-red transition-colors focus-within:outline-none focus-within:border-secondary-red">
        <input
          type="text"
          className="grow text-sm"
          placeholder={placeholder}
          value={orgQuery}
          onChange={e => {
            setOrgQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
        />
        <button
          onMouseDown={e => e.preventDefault()}
          onClick={() => {
            setIsOpen(true);
            onSearch();
          }}
          className="focus:outline-none"
        >
          <MagnifyingGlassIcon className="stroke-2 text-secondary-red h-5 w-5" aria-hidden="true" />
        </button>
      </label>
      {showResults && (
        <div className="absolute top-full left-0 right-0 bg-white shadow-lg rounded-xl z-10 mt-1 border border-slate-200 overflow-y-auto max-h-60">
          {orgs.map((org, index) => (
            <div
              key={org.org_id}
              ref={registerOption(index)}
              className={`px-4 py-2 cursor-pointer text-sm ${index === highlightedIndex ? 'bg-slate-100' : 'hover:bg-slate-100'}`}
              onMouseDown={e => e.preventDefault()}
              onMouseEnter={() => hoverHighlight(index)}
              onClick={() => onSelect(org)}
            >
              {org.org_name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default OrgSearchPicker;
